import { validateArrangement, checkSpecialHand } from '../../gameEngine';
import PlayerAvatar from './PlayerAvatar';
import Card from './Card';
import './PlayerSeat.css';

export default function PlayerSeat({
  player,
  uid,
  isDealer,
  isReady,
  isMe,
  phase,
  arrangement, // { bottom, middle, top } — only during showdown
  score,       // number — from roundResults
  showdownStep, // 0,1,2 for chi-by-chi
  gameMode,
  roundhouseAll, // true if this player swept all opponents
  onTap,       // tap to open comparison modal
  rotate = 0,  // degrees to rotate the entire seat (for left/right/corner positions)
  avatarBelow = false, // true = avatar below cards (bottom half of table)
  sessionTotal = 0,    // running total across all rounds
  rowScores = null,    // { bottom, middle, top } — per-row net scores for chi reveal
  autoWinEnabled = false, // whether special 13-card auto-wins are active this game
}) {
  const isSittingOut = player.sittingOut;
  const isSitOutQueued = player.sitOutQueued;
  const showCards = phase === 'showdown' || phase === 'results';
  const isChi = gameMode === 'chi' || gameMode === 'nemchi';

  // Detect auto-win (special hand) from arrangement cards — only when the
  // auto-win rule is active. Nem/Chi/Nem Chi default this OFF, so a legit but
  // ordinary hand like six pairs is scored normally, not flagged as an auto-win.
  const allCards = showCards && arrangement
    ? [...(arrangement.bottom || []), ...(arrangement.middle || []), ...(arrangement.top || [])]
    : [];
  const specialHand = autoWinEnabled && allCards.length === 13 ? checkSpecialHand(allCards) : null;

  // Detect foul from arrangement — auto-win hands are NOT fouls
  // Only check if arrangement has proper card counts (5+5+3)
  const hasFullArrangement = arrangement &&
    arrangement.bottom?.length === 5 &&
    arrangement.middle?.length === 5 &&
    arrangement.top?.length === 3;
  const isFouled = showCards && hasFullArrangement && !specialHand && !validateArrangement(arrangement).valid;

  // Chi-by-chi: which rows are visible (bottom first, then middle, then top)
  const visibleRows = (step) => {
    if (!isChi) return { top: true, middle: true, bottom: true };
    return {
      bottom: step >= 0,
      middle: step >= 1,
      top: step >= 2,
    };
  };

  const rowVisibility = visibleRows(showdownStep);

  const handleClick = () => {
    if (showCards && !isMe) onTap?.(uid);
  };

  const avatarElement = (
    <PlayerAvatar
      player={player}
      isDealer={isDealer}
      isReady={isReady}
      isSittingOut={isSittingOut}
      isSitOutQueued={isSitOutQueued}
    />
  );

  const cardsElement = (
    <div className="player-seat__inner">
      {/* Face-down stack while arranging */}
      {phase === 'arranging' && !isMe && !isSittingOut && (
        <div className="player-seat__card-stack">
          <Card faceDown size="small" />
        </div>
      )}

      {/* Revealed cards during showdown — bottom first for chi reveal */}
      {showCards && arrangement && (
        <div className={`player-seat__cards ${roundhouseAll ? 'player-seat__cards--roundhouse' : ''}`}>
          {/* avatarBelow = bottom-half seat: render top→middle→bottom (top row nearest table center)
              !avatarBelow = top-half seat: render bottom→middle→top so bottom row faces table center */}
          {(avatarBelow
            ? [
                { rowKey: 'top',    vis: rowVisibility.top,    rowClass: 'player-seat__row--top', score: rowScores?.top    },
                { rowKey: 'middle', vis: rowVisibility.middle, rowClass: '',                       score: rowScores?.middle },
                { rowKey: 'bottom', vis: rowVisibility.bottom, rowClass: '',                       score: rowScores?.bottom },
              ]
            : [
                { rowKey: 'bottom', vis: rowVisibility.bottom, rowClass: '',                       score: rowScores?.bottom },
                { rowKey: 'middle', vis: rowVisibility.middle, rowClass: '',                       score: rowScores?.middle },
                { rowKey: 'top',    vis: rowVisibility.top,    rowClass: 'player-seat__row--top', score: rowScores?.top    },
              ]
          ).map(({ rowKey, vis, rowClass, score: rScore }) => vis ? (
            <div key={rowKey} className="player-seat__row-with-score">
              <div className={`player-seat__row ${rowClass}`}>
                {(arrangement[rowKey] || []).map((card, i) => (
                  <Card key={card.id || i} card={card} size="small" disabled />
                ))}
              </div>
              {isChi && rScore !== undefined && (
                <span className={`player-seat__row-score ${rScore > 0 ? 'player-seat__score--positive' : rScore < 0 ? 'player-seat__score--negative' : 'player-seat__score--neutral'}`}>
                  {rScore > 0 ? `+${rScore}` : rScore}
                </span>
              )}
            </div>
          ) : null)}
          {/* Foul overlay — big red X */}
          {isFouled && <div className="player-seat__foul-overlay">✕</div>}
          {/* Auto-win indicator */}
          {specialHand && <div className="player-seat__autowin-overlay">{specialHand.name}</div>}
        </div>
      )}

    </div>
  );

  const showScore = (phase === 'showdown' || phase === 'results') && score !== undefined;
  const scoreElement = showScore ? (
    <div className="player-seat__scores">
      <span className={`player-seat__round-score ${score > 0 ? 'player-seat__score--positive' : score < 0 ? 'player-seat__score--negative' : 'player-seat__score--neutral'}`}>
        {score > 0 ? `+${score}` : score}
      </span>
      <span className={`player-seat__session-total ${sessionTotal > 0 ? 'player-seat__score--positive' : sessionTotal < 0 ? 'player-seat__score--negative' : 'player-seat__score--neutral'}`}>
        {sessionTotal > 0 ? `+${sessionTotal}` : sessionTotal}
      </span>
    </div>
  ) : null;

  const avatarWithScore = (
    <div className="player-seat__avatar-row">
      {avatarElement}
      {scoreElement}
    </div>
  );

  return (
    <div
      className={`player-seat ${isMe ? 'player-seat--me' : ''} ${roundhouseAll ? 'player-seat--roundhouse' : ''}`}
      style={{ transform: `rotate(${rotate}deg)` }}
      onClick={handleClick}
    >
      {/* avatarBelow: avatar goes after cards (bottom half of table) */}
      {/* !avatarBelow: avatar goes before cards (top half of table) */}
      {!avatarBelow && avatarWithScore}
      {cardsElement}
      {avatarBelow && avatarWithScore}

      {/* Roundhouse label */}
      {roundhouseAll && <div className="player-seat__roundhouse-label">ROUND HOUSE</div>}
    </div>
  );
}
